// src/components/pos/Stocks.jsx
// POS Stocks Component - Real inventory data integration

import { useState, useEffect, useCallback } from 'react'
import { Package, X, AlertTriangle, TrendingDown, Search, Filter, RefreshCw, ChevronDown, RotateCcw } from 'lucide-react'
import { cn } from '../../utils/cn'
import { POSButton } from './ui/Button'
import { SearchInput } from './ui/Input'
import ProductInventoryService from '../../services/productInventoryService'

const Stocks = ({ className, ...props }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all') // all, low_stock, out_of_stock, normal
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all') // all, active, inactive
  const [stockData, setStockData] = useState([])
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])

  // Get user info
  const user = JSON.parse(localStorage.getItem('tezlaa-user') || '{}')
  const userBranchId = user?.branchId || user?.branch?._id || user?.branch

  // Early return if no branch ID
  if (!userBranchId) {
    console.warn('⚠️ Stocks: No branch ID found in user data')
  }

  // Fetch all inventory data for the user's branch
  const fetchStockData = useCallback(async () => {
    if (!isOpen || !userBranchId) {
      if (!userBranchId) {
        console.warn('⚠️ Cannot fetch stocks: No branch ID found in user data')
      }
      return
    }

    try {
      setLoading(true)
      console.log('🔄 Fetching stock data for branch:', userBranchId)
      
      let allStocks = []
      let allCategories = new Set()
      
      // Fetch product inventories for this branch
      try {
        const productResponse = await ProductInventoryService.getInventoriesByBranch(userBranchId)
        
        if (productResponse?.success && productResponse?.data) {
          const productInventories = Array.isArray(productResponse.data) ? productResponse.data : 
                                   productResponse.data.productInventories || 
                                   productResponse.data.inventories || 
                                   productResponse.data.data || []
          
          // Transform and filter product inventories for this branch
          const productStocks = productInventories
            .filter(inventory => {
              // Ensure this inventory belongs to the user's branch
              const inventoryBranchId = inventory.branch?._id || inventory.branch
              if (inventoryBranchId !== userBranchId) return false
              
              // Filter out recipe-based products - only show raw materials and finished products
              const productType = inventory.product?.type || 'product'
              if (productType === 'recipe_based' || productType === 'recipe') return false
              
              return true
            })
            .map(inventory => {
              const currentStock = inventory.currentStock || 0
              const threshold = inventory.product?.threshold || inventory.threshold || 10
              const categoryName = inventory.product?.category?.name || 'Uncategorized'
              
              // Add to categories set
              allCategories.add(categoryName)
              
              return {
                _id: inventory._id,
                name: inventory.product?.name || 'Unknown Product',
                sku: inventory.product?.sku || inventory.product?.code || 'N/A',
                current: currentStock,
                minimum: threshold,
                unit: inventory.product?.unit?.abbreviation || 'units',
                itemType: 'Product',
                category: categoryName,
                isActive: inventory.isActive !== false && inventory.product?.isActive !== false,
                status: getStockStatusFromInventory(currentStock, threshold),
                unitPrice: inventory.unitPrice || 0,
                totalValue: (currentStock || 0) * (inventory.unitPrice || 0),
                productType: inventory.product?.type || 'product' // Add product type for filtering
              }
            })
          
          allStocks.push(...productStocks)
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch product inventories:', error)
      }

      setStockData(allStocks)
      setCategories(['All Categories', ...Array.from(allCategories).sort()])
      
    } catch (error) {
      console.error('❌ Error fetching stock data:', error)
      setStockData([])
    } finally {
      setLoading(false)
    }
  }, [isOpen, userBranchId])

  // Helper function to determine stock status
  const getStockStatusFromInventory = (currentStock, threshold) => {
    if (currentStock === 0) {
      return 'out_of_stock'
    } else if (currentStock <= threshold) {
      return 'low_stock'
    } else if (currentStock > threshold * 3) {
      return 'over_stock'
    } else {
      return 'normal'
    }
  }

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStockData()
    }
  }, [isOpen, fetchStockData])

  // Filter and sort stock data
  const filteredStock = stockData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesType = filterType === 'all' || item.status === filterType
    
    const matchesCategory = categoryFilter === 'all' || categoryFilter === 'All Categories' || 
                           item.category === categoryFilter
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && item.isActive) ||
                         (statusFilter === 'inactive' && !item.isActive)
    
    return matchesSearch && matchesType && matchesCategory && matchesStatus
  })

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('')
    setFilterType('all')
    setCategoryFilter('all')
    setStatusFilter('all')
  }

  // Calculate stock summary
  const stockSummary = {
    total: stockData.length,
    active: stockData.filter(item => item.isActive).length,
    inactive: stockData.filter(item => !item.isActive).length,
    low: stockData.filter(item => item.status === 'low_stock').length,
    out: stockData.filter(item => item.status === 'out_of_stock').length,
    normal: stockData.filter(item => item.status === 'normal').length,
    totalValue: stockData.reduce((sum, item) => sum + item.totalValue, 0)
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'out_of_stock': return 'Out of Stock'
      case 'low_stock': return 'Low Stock'
      case 'over_stock': return 'Over Stock'
      default: return 'Normal'
    }
  }

  return (
    <>
      {/* Trigger Button */}
      <POSButton
        variant="default"
        className={cn(
          "h-20 flex-col space-y-2 bg-gray-900 text-white hover:bg-gray-600 border-gray-700",
          className
        )}
        onClick={() => setIsOpen(true)}
        {...props}
      >
        <Package className="h-6 w-6" />
        <span className="text-sm font-medium">Stocks</span>
      </POSButton>

      {/* Stocks Popup */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Stock Management</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {stockSummary.total} items • {stockSummary.low + stockSummary.out} alerts
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Summary Cards */}
            <div className="p-6 border-b border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{stockSummary.total}</div>
                  <div className="text-sm text-blue-600">Total Items</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{stockSummary.normal}</div>
                  <div className="text-sm text-green-600">Normal Stock</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{stockSummary.low}</div>
                  <div className="text-sm text-yellow-600">Low Stock</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{stockSummary.out}</div>
                  <div className="text-sm text-red-600">Out of Stock</div>
                </div>
              </div>

              {/* Filter Section */}
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Filter</h4>
                
                {/* Clear Filters Button - Only show when filters are active */}
                {(searchTerm || filterType !== 'all' || categoryFilter !== 'all' || statusFilter !== 'all') && (
                  <POSButton
                    size="sm"
                    variant="outline"
                    onClick={clearAllFilters}
                    className="text-orange-500 border-orange-500 hover:bg-orange-50"
                  >
                    Clear Filters
                  </POSButton>
                )}
              </div>
              
              <div className="flex flex-wrap gap-4">
                {/* Search Input */}
                <div className="flex-1 min-w-[250px]">
                  {SearchInput ? (
                    <SearchInput
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClear={() => setSearchTerm('')}
                      placeholder="Search by name or SKU..."
                    />
                  ) : (
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name or SKU..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  )}
                </div>
                
                {/* Category Filter */}
                <div className="relative min-w-[150px]">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none bg-white"
                  >
                    {categories.map(category => (
                      <option key={category} value={category === 'All Categories' ? 'all' : category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                
                {/* Stock Status Filter */}
                <div className="relative min-w-[150px]">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none bg-white"
                  >
                    <option value="all">All Stock Levels</option>
                    <option value="normal">Normal Stock</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                    <option value="over_stock">Over Stock</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                
                {/* Active Status Filter */}
                <div className="relative min-w-[120px]">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>

                {/* Refresh Button */}
                <POSButton
                  size="sm"
                  variant="outline"
                  onClick={fetchStockData}
                  disabled={loading}
                  leftIcon={loading ? undefined : RefreshCw}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </POSButton>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-full py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mb-4" />
                  <p className="text-gray-500">Loading stock data...</p>
                </div>
              ) : (
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">Item</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">Category</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">Current Stock</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">Required Level</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">Value</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredStock.map(item => (
                          <tr key={item._id} className={cn(
                            "hover:bg-gray-50",
                            !item.isActive && "opacity-60"
                          )}>
                            <td className="py-4 px-4">
                              <div>
                                <div className="font-medium text-gray-900">{item.name}</div>
                                <div className="text-sm text-gray-500">{item.sku}</div>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-gray-900">{item.category}</td>
                            <td className="py-4 px-4">
                              <span className={cn(
                                "font-medium",
                                item.current === 0 ? "text-red-600" : 
                                item.current <= item.minimum ? "text-yellow-600" : 
                                "text-gray-900"
                              )}>
                                {item.current % 1 === 0 ? item.current : parseFloat(item.current).toFixed(2)} {item.unit}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-gray-900">
                              Min: {item.minimum % 1 === 0 ? item.minimum : parseFloat(item.minimum).toFixed(2)} {item.unit}
                            </td>
                            <td className="py-4 px-4 text-gray-900">
                              Rs. {item.totalValue.toLocaleString()}
                            </td>
                            <td className="py-4 px-4">
                              <span className={cn(
                                "px-3 py-1 rounded-full text-xs font-medium",
                                item.status === 'out_of_stock' ? "bg-red-100 text-red-800" :
                                item.status === 'low_stock' ? "bg-yellow-100 text-yellow-800" :
                                item.status === 'over_stock' ? "bg-green-100 text-green-800" :
                                "bg-blue-100 text-blue-800"
                              )}>
                                {getStatusLabel(item.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {filteredStock.length === 0 && !loading && (
                    <div className="text-center py-12">
                      <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">
                        {stockData.length === 0 ? 'No Stock Data' : 'No Items Found'}
                      </h4>
                      <p className="text-gray-500">
                        {stockData.length === 0 
                          ? userBranchId 
                            ? "No inventory data found for your branch."
                            : "Please ensure you are logged in with proper branch access."
                          : "No items match your current search and filter criteria."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Stocks